/* TrueMark engraving runtime. The HTML owner survives editor moves, while
   listeners, observers, frames and modal state are disposed on every removal. */
let activeEngravingOwner;

/* Laser look for the preview (Dylan, 2 Nov 2026: round 4 of the engraving-preview research,
   ~/clients/xinzuo/engraving-preview-methods-2026-10, tools/engrave.js r4Mark). The text is drawn as a
   mask, and inside it the blade photo is re-toned the way a laser marks that steel: one even charcoal
   tone on bright and Damascus steel (the pattern suppressed inside the letters, a hair bolder and with
   a thin bright cut edge on busy Damascus), pale silver-cream on black and hammered blades. Tones were
   measured from real engravings (the factory MICA logo, the Malpa "JHS" knife). Pure pixel work on the
   small region around the text; the caller falls back to the flat drawing if anything throws. */
const XZLaser = (() => {
    const clamp = (v, a = 0, b = 1) => v < a ? a : v > b ? b : v;
    const mix = (a, b, t) => a + (b - a) * t;
    const smooth = (e0, e1, x) => { const t = clamp((x - e0) / (e1 - e0)); return t * t * (3 - 2 * t); };
    const toLin = (v) => v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    const toS = (v) => { v = clamp(v); return v <= 0.0031308 ? v * 12.92 : 1.055 * Math.pow(v, 1 / 2.4) - 0.055; };
    function hash(x, y, s) { let h = (x * 374761393 + y * 668265263 + s * 982451653) | 0; h = (h ^ (h >>> 13)) * 1274126177 | 0; return ((h ^ (h >>> 16)) >>> 0) / 4294967295; }
    function vnoise(x, y, scale, s) {
        const fx = x / scale, fy = y / scale, ix = Math.floor(fx), iy = Math.floor(fy), tx = fx - ix, ty = fy - iy;
        const sx = tx * tx * (3 - 2 * tx), sy = ty * ty * (3 - 2 * ty);
        return mix(mix(hash(ix, iy, s), hash(ix + 1, iy, s), sx), mix(hash(ix, iy + 1, s), hash(ix + 1, iy + 1, s), sx), sy);
    }
    const gauss = (x, y, s) => (hash(x, y, s) + hash(x, y, s + 11) + hash(x, y, s + 23) - 1.5) * 2;
    const grain = (x, y, s) => 0.45 * gauss(x, y, s) + 1.6 * (vnoise(x, y, 1.5, s + 3) - 0.5) * 2 * 0.8;
    // Separable box blur, 3 passes (about a gaussian).
    function blur(src, w, h, r) {
        const a = Float32Array.from(src), b = new Float32Array(src.length), R = Math.max(1, Math.round(r));
        for (let pass = 0; pass < 3; pass++) {
            for (let y = 0; y < h; y++) {
                const o = y * w; let acc = 0;
                for (let x = -R; x <= R; x++) acc += a[o + clamp(x, 0, w - 1)];
                for (let x = 0; x < w; x++) { b[o + x] = acc / (2 * R + 1); acc += a[o + Math.min(w - 1, x + R + 1)] - a[o + Math.max(0, x - R)]; }
            }
            for (let x = 0; x < w; x++) {
                let acc = 0;
                for (let y = -R; y <= R; y++) acc += b[clamp(y, 0, h - 1) * w + x];
                for (let y = 0; y < h; y++) { a[y * w + x] = acc / (2 * R + 1); acc += b[Math.min(h - 1, y + R + 1) * w + x] - b[Math.max(0, y - R) * w + x]; }
            }
        }
        return a;
    }
    function gblur(src, w, h, sigma) { // true gaussian, sub-pixel sigma
        if (sigma < 0.05) return Float32Array.from(src);
        const r = Math.ceil(sigma * 3), k = []; let sum = 0;
        for (let i = -r; i <= r; i++) { const v = Math.exp(-i * i / (2 * sigma * sigma)); k.push(v); sum += v; }
        for (let i = 0; i < k.length; i++) k[i] /= sum;
        const a = new Float32Array(src.length), b = new Float32Array(src.length);
        for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) { let acc = 0; for (let i = -r; i <= r; i++) acc += src[y * w + clamp(x + i, 0, w - 1)] * k[i + r]; a[y * w + x] = acc; }
        for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) { let acc = 0; for (let i = -r; i <= r; i++) acc += a[clamp(y + i, 0, h - 1) * w + x] * k[i + r]; b[y * w + x] = acc; }
        return b;
    }
    function sample(f, w, h, x, y) {
        x = clamp(x, 0, w - 1.001); y = clamp(y, 0, h - 1.001);
        const ix = x | 0, iy = y | 0, tx = x - ix, ty = y - iy, i = iy * w + ix;
        return mix(mix(f[i], f[i + 1], tx), mix(f[i + w], f[i + w + 1], tx), ty);
    }
    // Crisp, antialiased growth of a mask by r px (the laser beam's width).
    function spread(src, w, h, r, near) {
        const out = Float32Array.from(src), offs = [];
        [r, r * 0.5].forEach((rr) => { for (let k = 0; k < 12; k++) { const t = k * Math.PI / 6; offs.push([Math.cos(t) * rr, Math.sin(t) * rr]); } });
        for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
            const i = y * w + x; if (near[i] < 0.001) continue;
            let m = out[i];
            for (let k = 0; k < offs.length; k++) { const v = sample(src, w, h, x + offs[k][0], y + offs[k][1]); if (v > m) m = v; }
            out[i] = m;
        }
        return out;
    }

    /* base: ImageData of the photo region at drawing resolution; mask: Float32Array (0-1) of the letters
       over the same region; fpx: the letters' font size in drawing pixels; px1: one source-photo pixel in
       drawing pixels. Returns ImageData: the marked pixels opaque, everything else transparent. */
    function mark(base, mask, fpx, px1) {
        const w = base.width, h = base.height, n = w * h, p = base.data;
        const L = new Float32Array(n);
        for (let i = 0; i < n; i++) L[i] = (0.2126 * p[i * 4] + 0.7152 * p[i * 4 + 1] + 0.0722 * p[i * 4 + 2]) / 255;
        const Lbig = blur(L, w, h, Math.max(8, fpx * 0.6)), Ls = blur(L, w, h, Math.max(3, fpx * 0.15));
        const steel = new Float32Array(n);
        for (let i = 0; i < n; i++) steel[i] = smooth(0.02, 0.055, Ls[i]);
        const under = [];
        for (let i = 0; i < n; i++) if (mask[i] > 0.5) under.push(Lbig[i]);
        under.sort((a, b) => a - b);
        const med = under.length ? under[under.length >> 1] : 0.4;
        // How busy the pattern is around the letters.
        const near = blur(mask, w, h, fpx * 0.25);
        let acc = 0, cnt = 0;
        for (let i = 0; i < n; i++) if (near[i] > 0.05 && steel[i] > 0.5) { acc += Math.abs(L[i] - Lbig[i]); cnt++; }
        const busy = cnt ? acc / cnt : 0;
        // The photo's grain (plain-steel residual) and the darkest pattern band around the letters.
        const sm = gblur(L, w, h, 1), near2 = blur(mask, w, h, Math.max(2, Math.round(fpx * 0.3))), sds = [], dv = [];
        for (let by = 0; by + 6 <= h; by += 6) for (let bx = 0; bx + 6 <= w; bx += 6) {
            let a = 0, a2 = 0, c = 0;
            for (let y = by; y < by + 6 && c >= 0; y++) for (let x = bx; x < bx + 6; x++) {
                const i = y * w + x; if (steel[i] < 0.9 || near2[i] > 0.02) { c = -1; break; }
                const r = L[i] - sm[i]; a += r; a2 += r * r; c++;
            }
            if (c > 20) sds.push(Math.sqrt(Math.max(0, a2 / c - (a / c) * (a / c))));
        }
        sds.sort((a, b) => a - b);
        const noise = clamp(sds.length ? sds[Math.floor(sds.length * 0.2)] * 1.6 : 0.01, 0.002, 0.02) / px1;
        for (let i = 0; i < n; i += 2) if (steel[i] > 0.9 && near2[i] > 0.05 && mask[i] < 0.02) dv.push(L[i]);
        dv.sort((a, b) => a - b);
        const darkBand = dv.length ? dv[Math.floor(dv.length * 0.1)] : 0.3;

        const champagne = med < 0.3;
        const F = smooth(0.07, 0.12, busy); // 0 on polished or satin steel, 1 on busy Damascus
        const beam = mix(0.011, 0.028, F) * fpx;
        const soft = gblur(beam > 0.15 ? spread(mask, w, h, beam, blur(mask, w, h, Math.ceil(beam + 1))) : mask, w, h, 0.32);
        // A hair-thin bright ring just outside charcoal letters on busy steel: the laser's clean cut.
        const kerfA = champagne ? 0 : mix(0, 0.75, F);
        let ring = null;
        if (kerfA > 0) {
            const wide = gblur(spread(soft, w, h, 1.1 * px1, blur(mask, w, h, Math.ceil(1.1 * px1 + 1))), w, h, 0.45 * px1);
            ring = wide.map((v, i) => clamp(v - soft[i]));
        }
        const K = mix(0.256, 0.08, smooth(0.62, 0.82, med));
        const dark = mix(0.5, 0.3, F) * toLin(darkBand);
        const tone = blur(L, w, h, Math.max(6, fpx * 0.9));
        const CH = [0.78, 0.75, 0.687];
        const out = new ImageData(w, h), o = out.data;
        for (let y = 0, i = 0; y < h; y++) for (let x = 0; x < w; x++, i++) {
            const a = soft[i] * steel[i], r = ring ? ring[i] * steel[i] * kerfA : 0;
            if (a < 0.004 && r < 0.004) continue;
            let pr = p[i * 4] / 255, pg = p[i * 4 + 1] / 255, pb = p[i * 4 + 2] / 255;
            if (r > 0.004) { const lift = mix(pg, Math.min(1, tone[i] * 1.18 + 0.1), r); pr = pg = pb = lift; }
            const g = grain(x, y, 7) * noise * 0.45;
            let cr, cg, cb;
            if (!champagne) {
                const bl = toLin(tone[i]);
                const ghost = F > 0.5 ? 1 : clamp(Math.pow(toLin(L[i]) / Math.max(1e-3, bl), 0.6), 0.85, 1.18);
                cr = cg = cb = toS(Math.min(K * bl, dark) * ghost) + g;
            } else {
                const sheen = 1 + 0.05 * (vnoise(x, y, Math.max(2, fpx * 0.22), 5) - 0.5) * 2;
                const k = sheen * mix(1, clamp(tone[i] / Math.max(0.05, med), 0.7, 1.3), 0.1), gc = g * 0.5;
                cr = CH[0] * k + gc; cg = CH[1] * k + gc; cb = CH[2] * k + gc;
            }
            const t = clamp(a);
            o[i * 4] = clamp(mix(pr, cr, t)) * 255;
            o[i * 4 + 1] = clamp(mix(pg, cg, t)) * 255;
            o[i * 4 + 2] = clamp(mix(pb, cb, t)) * 255;
            o[i * 4 + 3] = 255;
        }
        return out;
    }
    return { mark };
})();

/** Unpublished v2 QA opt-in never overrides the published app selection. */
function engravingPreviewAllowed(container, shopify = window.Shopify || {}) {
    const theme = shopify.theme || {};
    const role = String(theme.role || '').toLowerCase();
    if (role === 'development') return true;
    if (role === 'unpublished' && container.dataset.tmPreviewOptIn === 'true' && container.closest('xz-v2-pdp')) return true;
    const selected = (container.dataset.tmThemeId || '').trim();
    return !!(selected && theme.id != null && selected === String(theme.id));
}

/* The engraving text rule the hint under the field states: letters, numbers, spaces and . , ! ? ' -
   Accented letters are folded to their plain letter first (é to e, ö to o, so José stays Jose instead
   of losing a letter), then phone keyboards' smart punctuation is mapped back (curly and prime
   apostrophes to ', curly double quotes to " (not engravable, so it then goes), en and em dashes to -,
   a no-break space to a space); anything else is dropped, runs of spaces collapse to one and a leading
   space goes. A trailing space stays while typing; product-form.js trims the cart properties.
   Keep in step with the ENGRAVING_* rule in assets/cart-engraving.js. */
const ENGRAVING_ALLOWED_CHAR = /[A-Za-z0-9 .,!?'\-]/;
function foldEngravingAccents(text) {
    const value = String(text || '');
    return typeof value.normalize === 'function' ? value.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : value;
}
function mapEngravingPunctuation(text) {
    return foldEngravingAccents(text)
        .replace(/[\u2018\u2019\u2032]/g, "'")
        .replace(/[\u201C\u201D]/g, '"')
        .replace(/[\u2013\u2014]/g, '-')
        .replace(/\u00A0/g, ' ');
}
function sanitiseEngravingText(text) {
    return mapEngravingPunctuation(text)
        .replace(/[^A-Za-z0-9 .,!?'\-]/g, '')
        .replace(/ {2,}/g, ' ')
        .replace(/^ /, '');
}
/* True when the text holds a character the rule drops (spaces only collapse, so they never count). */
function engravingHasDisallowed(text) {
    return Array.from(mapEngravingPunctuation(text)).some((ch) => !ENGRAVING_ALLOWED_CHAR.test(ch));
}

function bindEngravingLens(stage, lensEl, listen, frame, LENS_SIZE, LENS_ZOOM) {
    if (!stage || !lensEl) return () => {};
            let lensFrame = null;
            let lensPoint = null;
            let lastTouch = -Infinity;

            function applyLensPosition() {
                lensFrame = null;
                if (!lensPoint || !lensEl.style.backgroundImage) return;
                const half = LENS_SIZE / 2;
                const x = lensPoint.x, y = lensPoint.y;
                // A mouse lens is centred on the cursor. A finger would cover that centre, so a touch
                // lens sits above the finger (64px), kept inside the stage, which clips (overflow:hidden).
                let lx = x, ly = y;
                if (lensPoint.touch) {
                    const clamp = (v, lo, hi) => (hi < lo ? (lo + hi) / 2 : Math.min(hi, Math.max(lo, v)));
                    lx = clamp(x, half, lensPoint.w - half);
                    ly = clamp(y - 64, half, lensPoint.h - half);
                }
                lensEl.style.transform =
                    "translate3d(" + (lx - half) + "px, " + (ly - half) + "px, 0)";
                lensEl.style.backgroundSize =
                    (lensPoint.w * LENS_ZOOM) + "px " + (lensPoint.h * LENS_ZOOM) + "px";
                lensEl.style.backgroundPosition =
                    (-x * LENS_ZOOM + half) + "px " + (-y * LENS_ZOOM + half) + "px";
                lensEl.classList.add("is-visible");
            }

            const reset = () => {
                lensEl.classList.remove("is-visible");
                lensPoint = null;
            };
            const recordPointer = (e, touch) => {
                const rect = stage.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const y = e.clientY - rect.top;
                if (x < 0 || y < 0 || x > rect.width || y > rect.height) {
                    lensEl.classList.remove("is-visible");
                    lensPoint = null;
                    return;
                }
                lensPoint = { x: x, y: y, w: rect.width, h: rect.height, touch: !!touch };
                if (lensFrame === null) lensFrame = frame(applyLensPosition);
            };
            // Mouse: unchanged. The compatibility mouse events a browser sends after a tap are ignored,
            // so a lifted finger does not leave the lens parked where it tapped.
            const recordMouse = (e) => {
                if (Date.now() - lastTouch < 800) return;
                recordPointer(e, false);
            };
            listen(stage, "mouseenter", recordMouse);
            listen(stage, "mousemove", recordMouse);
            listen(stage, "mouseleave", reset);

            // Touch and pen: dragging a finger or stylus over the preview shows the lens; lifting it
            // hides the lens. openModal() sets touch-action:none on the stage while the preview is open,
            // so the drag moves the lens instead of scrolling.
            if (window.PointerEvent) {
                let activePointer = null;
                const isTouch = (e) => e.pointerType === "touch" || e.pointerType === "pen";
                listen(stage, "pointerdown", (e) => {
                    if (!isTouch(e)) return;
                    lastTouch = Date.now();
                    activePointer = e.pointerId;
                    try { stage.setPointerCapture(e.pointerId); } catch (err) {}
                    recordPointer(e, true);
                });
                listen(stage, "pointermove", (e) => {
                    if (!isTouch(e) || e.pointerId !== activePointer) return;
                    lastTouch = Date.now();
                    recordPointer(e, true);
                });
                const end = (e) => {
                    if (!isTouch(e) || e.pointerId !== activePointer) return;
                    lastTouch = Date.now();
                    activePointer = null;
                    reset();
                };
                listen(stage, "pointerup", end);
                listen(stage, "pointercancel", end);
            }
            return reset;
}

/** Keyboard ownership for the existing custom engraving dialog. */
function engravingModalFocus(modal, activeElement = () => document.activeElement) {
    let opener;
    const candidates = () => Array.from(modal.querySelectorAll('button, a[href], input, select, textarea, [tabindex]'))
        .filter(el => !el.disabled && el.tabIndex !== -1 && el.getClientRects().length);
    const focusFirst = () => {
        const first = candidates()[0];
        if (first) first.focus();
        else { modal.setAttribute('tabindex', '-1'); modal.focus(); }
    };
    return {
        open() { opener = activeElement(); focusFirst(); },
        close() { if (opener?.isConnected) opener.focus(); opener = null; },
        contain() { if (!modal.contains(activeElement())) focusFirst(); },
        keydown(event) {
            if (event.key !== 'Tab') return;
            const items = candidates(), active = activeElement();
            if (!items.length) { event.preventDefault(); focusFirst(); return; }
            const first = items[0], last = items[items.length - 1];
            if (!modal.contains(active) || (event.shiftKey && active === first) || (!event.shiftKey && active === last)) {
                event.preventDefault(); (event.shiftKey ? last : first).focus();
            }
        }
    };
}

/** V2 owns presentation only: relocate the existing form control and delegate to native preview. */
function bindV2EngravingPreview(container, fontGroup, modal, signal, allowed, canPreview, open) {
    if (!container.closest('xz-v2-pdp') || !modal) return;
    const body = modal.querySelector('.engraving-modal__body');
    const originalParent = fontGroup?.parentNode;
    const originalNext = fontGroup?.nextSibling;
    if (body && fontGroup) body.prepend(fontGroup);
    container.addEventListener('engraving:preview-request', (event) => {
        if (!allowed || !canPreview()) return;
        event.preventDefault();
        open();
    }, { signal });
    signal.addEventListener('abort', () => {
        if (originalParent && fontGroup) originalParent.insertBefore(fontGroup,
            originalNext?.parentNode === originalParent ? originalNext : null);
    }, { once: true });
}

function initializeEngraving(container, signal) {
    activeEngravingOwner = container;
    const get = (id) => container.querySelector(`[id="${id}"]`);
    const listen = (target, type, handler, options = {}) => target?.addEventListener(type, handler, { ...options, signal });
    const frames = new Set();
    const frame = (callback) => {
        if (signal.aborted) return null;
        const id = requestAnimationFrame(() => {
            frames.delete(id);
            if (!signal.aborted && container.isConnected) callback();
        });
        frames.add(id);
        return id;
    };
    signal.addEventListener('abort', () => frames.forEach(cancelAnimationFrame), { once: true });
    // The text field takes focus only when the switch/buttons were activated from the keyboard. From a tap,
    // focus opens the on-screen keyboard (on Android it also resizes the viewport) the moment the switch is
    // touched; Milen's switch focuses nothing. Keyboard users still land in the field they just opened.
    let keyboardActivation = false;
    listen(document, 'keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') keyboardActivation = true;
    }, { capture: true });
    listen(document, 'pointerdown', () => { keyboardActivation = false; }, { capture: true });
    const focusFromKeyboard = (input) => { if (keyboardActivation) frame(() => input.focus()); };

        // On this owner's first start the panel is hidden (as the markup renders it), so a checked box or a
        // two-line radio can only be a browser form restore (Back), never a choice the shopper can see: start
        // off, one line. A moved owner (editor) keeps its live state when it reconnects.
        const firstStart = !container.__xzEngravingStarted;
        container.__xzEngravingStarted = true;
        if (firstStart && get("engrave-div")?.style.display === "none") {
            if (get("engrave-state")) get("engrave-state").checked = false;
            if (get("engrave-one")) get("engrave-one").checked = true;
            if (get("engrave-two")) get("engrave-two").checked = false;
        }

        // Global state
        window.engravingSelected = !!get("engrave-state")?.checked;
        window.engravingSecondSelected = !!get("engrave-two")?.checked;
        // A value the browser restored into the boxes is held to the same text rule as typing.
        ["engravedInput", "engravedInput2"].forEach((id) => {
            const box = get(id);
            if (box && box.value && sanitiseEngravingText(box.value) !== box.value) box.value = sanitiseEngravingText(box.value);
        });
        window.engravingText = get("engravedInput")?.value || "";
        window.engravingText2 = get("engravedInput2")?.value || "";
        window.knife_num = Number(container.dataset.knifeQuantity) || 1;

        // DOM elements
        const engraveDiv = get("engrave-div");
        const engraveCheck = get("engrave-state");
        const engraveRadio = get("engrave-one");
        const engraveSecondRadio = get("engrave-two");
        const engraveInput = get("engravedInput");
        const engraveSecondInput = get("engravedInput2");
        const countLine1 = get("count-line1");
        const countLine2 = get("count-line2");
        const inputGroup2 = get("input-group-2");

        if (!engraveDiv || !engraveCheck || !engraveInput || !engraveSecondInput) return;

        // New button elements
        const btnNoEngraving = get("btn-no-engraving");
        const btnAddEngraving = get("btn-add-engraving");
        const btnOneLine = get("btn-one-line");
        const btnTwoLines = get("btn-two-lines");

        // Customer font chooser. The dropdown shows friendly labels
        // ("Plain"/"Fancy") but its value — the real font name saved on the
        // order — is "Montserrat"/"Bonheur Royale". FONT_NAME_TO_KEY maps that
        // name to the FONT_MAP key the canvas render uses. fontDefaultValue
        // captures the admin-configured default so we can reset to it.
        const fontSelect = get("engraving-font-select");
        const fontGroup = get("engraving-font-group");
        const fontDefaultValue = fontSelect ? fontSelect.value : "Montserrat";
        const FONT_NAME_TO_KEY = { "Montserrat": "montserrat", "Bonheur Royale": "bonheur-royale" };
        // Publish the chosen font name as a global so product-form.js includes it
        // in the order properties — it builds the cart request from window.*
        // globals, not from the form inputs.
        window.engravingFont = fontDefaultValue;

        // Bonheur Royale always opens on a capital. The face is a formal script
        // whose swashes live on the capitals, and a lowercase opening letter
        // reads as a mistake on a blade. Montserrat is left exactly as typed —
        // this is a property of the typeface, not a house style.
        //
        // The stored value is rewritten, not just the pixels: product-form.js
        // builds the cart properties from window.engravingText, so a display-only
        // transform (CSS text-transform) would show the customer one thing and
        // engrave another. Mirrors capitaliseForFont() in
        // engraving-admin/app/lib/engraving-geometry.js — keep the two in step.
        const AUTO_CAPITAL_FONTS = { "bonheur-royale": true };

        function capitaliseForFont(text, fontKey) {
            if (!text || !AUTO_CAPITAL_FONTS[fontKey]) return text;
            const i = text.search(/\S/);
            if (i < 0) return text;
            const up = text[i].toUpperCase();
            // A few letters uppercase to two characters (ß -> SS). Growing the
            // string would break maxlength and the character counter, so skip them.
            if (up === text[i] || up.length !== 1) return text;
            return text.slice(0, i) + up + text.slice(i + 1);
        }

        // The face currently chosen, resolved exactly as drawEngraving resolves
        // it. One value for both lines.
        function currentFontKey() {
            const key = fontSelect && FONT_NAME_TO_KEY[fontSelect.value];
            return key || SETTINGS.font || DEFAULT_FONT_KEY;
        }

        // Screen readers hear why a typed character did not appear (the hint above the box states the
        // rule); visually nothing changes. Polite, and cleared first so a repeat is announced again. The
        // region is put in place when a box first takes focus, so it exists before it ever speaks.
        let ruleLive = null;
        let ruleTimer = null;
        function ensureRuleLive() {
            if (ruleLive && ruleLive.isConnected) return ruleLive;
            ruleLive = engraveDiv.querySelector('[data-engraving-rule-live]');
            if (!ruleLive) {
                ruleLive = document.createElement('span');
                ruleLive.className = 'visually-hidden';
                ruleLive.setAttribute('aria-live', 'polite');
                ruleLive.setAttribute('data-engraving-rule-live', '');
                engraveDiv.appendChild(ruleLive);
            }
            return ruleLive;
        }
        [engraveInput, engraveSecondInput].forEach((input) => listen(input, 'focus', ensureRuleLive));
        signal.addEventListener('abort', () => { clearTimeout(ruleTimer); if (ruleLive) ruleLive.remove(); }, { once: true });
        function announceRule() {
            const live = ensureRuleLive();
            live.textContent = '';
            clearTimeout(ruleTimer);
            ruleTimer = setTimeout(() => {
                live.textContent = "Only letters, numbers, spaces and . , ! ? ' - can be engraved.";
            }, 60);
        }

        // Apply the text rule (sanitiseEngravingText) and the font's capital rule to a text box in
        // place, returning the value to publish. The box is rewritten only when it actually differs,
        // and the caret is put back where it was (less whatever was dropped before it) — otherwise
        // fixing a typo mid-string would throw the cursor to the end on every keystroke. While a
        // keyboard composition is open (Android word suggestions, IMEs) the box is left alone, since
        // rewriting it breaks the composition; the cleaned value is still published, and the
        // compositionend handler then applies it to the box.
        function enforceFontCase(input, composing) {
            if (!input) return "";
            const value = input.value;
            const next = capitaliseForFont(sanitiseEngravingText(value), currentFontKey());
            if (composing || next === value) return next;
            const focused = document.activeElement === input;
            const start = input.selectionStart;
            const end = input.selectionEnd;
            input.value = next;
            if (focused && start !== null && start !== undefined) {
                const map = (pos) => Math.min(next.length, sanitiseEngravingText(value.slice(0, pos)).length);
                try { input.setSelectionRange(map(start), map(end)); } catch (err) {}
            }
            if (engravingHasDisallowed(value)) announceRule();
            return next;
        }

        if (!engraveDiv || !engraveCheck || !engraveInput || !engraveSecondInput) {
            console.warn('Engraving elements not found');
            return;
        }

        // Helper to reset engraving state
        function resetEngraving() {
            const maxLength = Number(container.dataset.maxLength) || 20;
            engraveDiv.style.display = "none";
            engraveCheck.checked = false;
            window.engravingSelected = false;
            window.engravingSecondSelected = false;
            window.engravingText = "";
            window.engravingText2 = "";
            engraveInput.value = "";
            engraveSecondInput.value = "";
            if (countLine1) countLine1.textContent = `0/${maxLength}`;
            if (countLine2) countLine2.textContent = `0/${maxLength}`;
            engraveRadio.checked = true;
            engraveSecondRadio.checked = false;
            inputGroup2.style.display = "none";

            // Reset line buttons
            btnOneLine.classList.add('selected');
            btnTwoLines.classList.remove('selected');

            // Reset the font chooser back to its admin default.
            if (fontSelect) {
                fontSelect.value = fontDefaultValue;
                window.engravingFont = fontDefaultValue;
            }
        }

        // No Engraving button
        listen(btnNoEngraving, "click", () => {
            btnNoEngraving.classList.add('selected');
            btnAddEngraving.classList.remove('selected');
            resetEngraving();
        });

        // Add Engraving button
        listen(btnAddEngraving, "click", () => {
            btnAddEngraving.classList.add('selected');
            btnNoEngraving.classList.remove('selected');
            engraveDiv.style.display = container.closest('xz-v2-pdp') ? "flex" : "block";
            engraveCheck.checked = true;
            window.engravingSelected = true;
            // Keyboard activation only (no on-screen keyboard from a tap)
            focusFromKeyboard(engraveInput);
        });

        // One Line button
        listen(btnOneLine, "click", () => {
            btnOneLine.classList.add('selected');
            btnTwoLines.classList.remove('selected');
            engraveRadio.checked = true;
            engraveSecondRadio.checked = false;
            inputGroup2.style.display = "none";
            window.engravingSecondSelected = false;
            window.engravingText2 = "";
            engraveSecondInput.value = "";
            updateCharacterCount();
        });

        // Two Lines button
        listen(btnTwoLines, "click", () => {
            btnTwoLines.classList.add('selected');
            btnOneLine.classList.remove('selected');
            engraveSecondRadio.checked = true;
            engraveRadio.checked = false;
            inputGroup2.style.display = "flex";
            window.engravingSecondSelected = true;
            focusFromKeyboard(engraveSecondInput);
            updateCharacterCount();
        });

        // Update character count for each line
        function updateCharacterCount() {
            const maxLength = Number(container.dataset.maxLength) || 20;

            // Update line 1 counter
            if (countLine1) {
                countLine1.textContent = `${window.engravingText.length}/${maxLength}`;
            }

            // Update line 2 counter (if two-line is selected)
            if (countLine2 && window.engravingSecondSelected) {
                countLine2.textContent = `${window.engravingText2.length}/${maxLength}`;
            }
        }

        /* ====== TrueMark engraving preview (admin-driven, fixed position) ====== */

        // The customer types text and clicks Preview. Position, font, color,
        // size, and rotation are all admin-set per-product metafields and
        // are never customer-controlled. The render below draws the typed
        // text onto the blade mockup with a subtle emboss to look engraved.

        let scheduleRender = () => {};

        // The counters, the preview and the cart all read the cleaned value.
        function onLineInput(input, composing) {
            const value = enforceFontCase(input, composing);
            if (input === engraveInput) window.engravingText = value;
            else window.engravingText2 = value;
            updateCharacterCount();
            updatePreviewButtonState();
            scheduleRender();
        }
        [engraveInput, engraveSecondInput].forEach((input) => {
            listen(input, "input", (e) => onLineInput(input, !!e.isComposing));
            listen(input, "compositionend", () => onLineInput(input, false));
            // A paste is cleaned before the length limit cuts it. The native maxlength would cut the
            // raw text first, so characters the rule drops (& # and the like) cost letters at the end
            // ('Chef & Dylan’s #1 Knife' left 'Chef Dylan's 1 Kn'). The cleaned text goes in at the
            // caret, trimmed so the whole box stays within data-max-length, and the input event then
            // runs the usual rule, capital, counter, preview and window.engravingText updates.
            listen(input, "paste", (e) => {
                const clip = e.clipboardData;
                if (!clip || typeof input.setRangeText !== "function") return;
                const raw = clip.getData("text/plain");
                e.preventDefault();
                const max = Number(container.dataset.maxLength) || (input.maxLength > 0 ? input.maxLength : 20);
                const value = input.value;
                const start = input.selectionStart == null ? value.length : input.selectionStart;
                const end = input.selectionEnd == null ? start : input.selectionEnd;
                const flat = String(raw || "").replace(/[\r\n\t]+/g, " ");
                const cleaned = mapEngravingPunctuation(flat)
                    .replace(/[^A-Za-z0-9 .,!?'\-]/g, "")
                    .replace(/ {2,}/g, " ");
                const room = Math.max(0, max - (value.length - (end - start)));
                input.setRangeText(cleaned.slice(0, room), start, end, "end");
                input.dispatchEvent(new Event("input", { bubbles: true }));
                if (engravingHasDisallowed(flat)) announceRule();
            });
        });

        const stage         = get("engraving-preview-stage");
        const canvas        = get("engraving-preview-canvas");
        const mockupImg     = get("engraving-preview-mockup");
        const placeholder   = get("engraving-preview-placeholder");
        const lensEl        = get("engraving-preview-lens");
        let resetLens = () => {};

        const previewOpenBtn = get("engraving-preview-open");
        const downloadBtn   = get("engraving-preview-download");
        const modal         = get("engraving-modal");
        const modalFocus = modal ? engravingModalFocus(modal) : null;
        const modalCloseEls = modal ? modal.querySelectorAll("[data-engraving-modal-close]") : [];

        // Engraving-preview theme gate. On published (live) themes the preview
        // is available only on the theme selected in the Engraving Admin app
        // (shop metafield custom.engraving_theme_id, surfaced here as
        // data-tm-theme-id):
        //   • a specific theme selected → available only on that theme
        //   • "Hidden" selected (empty)  → off on every published theme
        // Development themes remain testable. Editor mode alone never bypasses
        // the app selection; the explicit unpublished v2 opt-in is separate.
        //
        // The gate covers the "Preview Engraving" button AND the Plain / Fancy
        // font chooser. The chooser exists only to drive that preview, so
        // offering a face the customer cannot see applied is worse than not
        // offering one — they would be picking blind. Everything else (the
        // engraving toggles, the text inputs, the fee) is untouched: engraving
        // still sells on every theme, it just uses the face the merchant
        // configured rather than one the customer picks.
        // A theme-owned opt-in additionally enables QA only inside v2 shopping
        // on unpublished themes. Publishing removes that exception automatically.
        const engravingPreviewEnabled = engravingPreviewAllowed(container);
        if (container.closest('xz-v2-pdp') && engraveDiv.style.display === "block") engraveDiv.style.display = "flex";

        if (previewOpenBtn) {
            previewOpenBtn.style.display = engravingPreviewEnabled ? "block" : "none";
        }
        // Hidden rather than removed: window.engravingFont keeps the merchant's
        // configured default, so the order still records a font and the select
        // still carries its value if the form is ever submitted natively.
        if (fontGroup) {
            fontGroup.style.display = engravingPreviewEnabled ? "flex" : "none";
        }

        const LENS_SIZE = 160;
        const LENS_ZOOM = 2;
        // Longest edge of the lens snapshot, in device pixels.
        const MAX_LENS_SNAPSHOT_PX = 2400;

        function hasAnyEngravingText() {
            const t1 = (window.engravingText || "").trim();
            const t2 = window.engravingSecondSelected ? (window.engravingText2 || "").trim() : "";
            return !!(t1 || t2);
        }

        function updatePreviewButtonState() {
            if (previewOpenBtn) previewOpenBtn.disabled = !hasAnyEngravingText();
            if (downloadBtn)    downloadBtn.disabled    = !hasAnyEngravingText();
        }

        // Composite the mockup image + the engraving into one PNG and trigger a
        // browser download.
        //
        // The export is sized from the MOCKUP's own resolution, not from the
        // on-screen preview box. Sizing it to the box (as this used to) capped
        // the file at the modal's ~596px width and threw away most of the
        // source image, which is fetched at width: 1400. `ss = 1/fit` undoes the
        // on-screen fit so the mockup lands on its own pixels 1:1; the
        // devicePixelRatio floor keeps Retina exports from ever getting smaller
        // than what is on screen.
        //
        // The engraving is re-rendered through drawEngraving() rather than
        // blitting the display canvas — that canvas is only display resolution,
        // so copying it would bake in soft, upscaled lettering. drawEngraving
        // works in CSS-pixel coordinates against a scaled context, so the text
        // re-rasterises crisply at the export scale and lands in exactly the
        // same place, per-line fonts included.
        function downloadPreview() {
            if (!canvas || !mockupImg || !mockupImg.complete) return;
            try {
                const stageRect = stage.getBoundingClientRect();
                const cw  = stageRect.width;
                const ch  = stageRect.height;
                const mw  = mockupImg.naturalWidth;
                const mh  = mockupImg.naturalHeight;

                const fit = (mw && mh) ? Math.min(cw / mw, ch / mh) : 1;
                const ss  = Math.max(window.devicePixelRatio || 1, fit > 0 ? 1 / fit : 1);

                const out = document.createElement("canvas");
                out.width  = Math.max(1, Math.round(cw * ss));
                out.height = Math.max(1, Math.round(ch * ss));
                const octx = out.getContext("2d");
                octx.scale(ss, ss);
                octx.imageSmoothingEnabled = true;
                octx.imageSmoothingQuality = "high";

                // Mockup, fitted the same way as the live render.
                if (mw && mh) {
                    const drawW = mw * fit;
                    const drawH = mh * fit;
                    octx.drawImage(mockupImg, (cw - drawW) / 2, (ch - drawH) / 2, drawW, drawH);
                }
                // Engraving, re-rendered at the export scale.
                paintEngraving(octx, cw, ch, ss);

                out.toBlob((blob) => {
                    if (!blob) return;
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "engraving-preview.png";
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    setTimeout(() => URL.revokeObjectURL(url), 1000);
                }, "image/png");
            } catch (err) {
                // CORS-tainted canvas or other failure — fall back silently.
                console.warn("Engraving download failed:", err);
            }
        }

        if (downloadBtn) {
            listen(downloadBtn, "click", () => {
                if (downloadBtn.disabled) return;
                downloadPreview();
            });
        }

        // What the preview was opened with. Closing it any way but Done (Cancel, X, the backdrop,
        // Escape) puts the face and the text back, so a font tried in the preview and then
        // cancelled is not what the order records (nor the capital Fancy gave the text).
        let modalSnapshot = null;
        // When the preview last opened. A double-click on the panel's DONE opens the preview on the
        // first click; at 1680 and wider DONE sits outside the centred dialog, so the second click
        // lands on the backdrop and used to cancel straight away (r4: preview flashed open and shut).
        let modalOpenedAt = -Infinity;
        function openModal() {
            if (!modal) return;
            if (!modal.classList.contains("is-open")) {
                modalOpenedAt = performance.now();
                modalSnapshot = {
                    font: fontSelect ? fontSelect.value : null,
                    engravingFont: window.engravingFont,
                    t1: engraveInput.value,
                    t2: engraveSecondInput.value,
                    g1: window.engravingText,
                    g2: window.engravingText2
                };
            }
            modal.classList.add("is-open");
            modal.setAttribute("aria-hidden", "false");
            document.body.classList.add("engraving-modal-open");
            // A finger dragged over the preview moves the magnifier (bindEngravingLens) instead of
            // scrolling; set only while the preview is open, so the page scrolls as usual otherwise.
            if (stage) stage.style.touchAction = "none";
            modalFocus?.open();
            frame(() => {
                sizeCanvas();
                render();
            });
        }

        function restoreModalSnapshot() {
            const snap = modalSnapshot;
            modalSnapshot = null;
            if (!snap) return;
            if (fontSelect && snap.font !== null) fontSelect.value = snap.font;
            window.engravingFont = snap.engravingFont;
            engraveInput.value = snap.t1;
            engraveSecondInput.value = snap.t2;
            window.engravingText = snap.g1;
            window.engravingText2 = snap.g2;
            updateCharacterCount();
            updatePreviewButtonState();
            scheduleRender();
        }

        function closeModal(confirmed) {
            if (!modal) return;
            if (confirmed) modalSnapshot = null;
            else if (modal.classList.contains("is-open")) restoreModalSnapshot();
            resetLens();
            if (stage) stage.style.removeProperty("touch-action");
            modal.classList.remove("is-open");
            modal.setAttribute("aria-hidden", "true");
            document.body.classList.remove("engraving-modal-open");
            modalFocus?.close();
        }

        if (previewOpenBtn) {
            listen(previewOpenBtn, "click", () => {
                if (previewOpenBtn.disabled) return;
                openModal();
            });
        }

        bindV2EngravingPreview(container, fontGroup, modal, signal, engravingPreviewEnabled,
            () => !!engraveInput.value.trim() && (!window.engravingSecondSelected || !!engraveSecondInput.value.trim()), openModal);
        const backdropEl = modal ? modal.querySelector(".engraving-modal__backdrop") : null;
        modalCloseEls.forEach((el) => listen(el, "click", (e) => {
            // The backdrop (only; X, Cancel, Done and Escape are unchanged) ignores the tail of the
            // double-click that opened the preview: a click within 500 ms of opening, or the second
            // click of a double-click (detail 2+), whose first click was not on the backdrop or the
            // preview would already be closed. A deliberate backdrop click still cancels.
            if (el === backdropEl && (performance.now() - modalOpenedAt < 500 || e.detail > 1)) return;
            const confirmed = el.hasAttribute('data-engraving-modal-confirm');
            closeModal(confirmed);
            if (confirmed) {
                container.dispatchEvent(new Event('engraving:confirmed'));
            }
        }));

        listen(document, 'focusin', (e) => {
            if (e.target.closest?.('[data-kl-scroll-locking-modal]')) return;
            if (modal?.classList.contains('is-open')) modalFocus?.contain();
        });
        listen(document, "keydown", (e) => {
            if (e.target.closest?.('[data-kl-scroll-locking-modal]')) return;
            if (modal?.classList.contains('is-open')) modalFocus?.keydown(e);
            if (e.key === "Escape" && modal && modal.classList.contains("is-open")) {
                e.preventDefault();
                closeModal(false);
            }
        });

        if (!canvas || !mockupImg || !container) return;

        const FONT_MAP = {
            "bonheur-royale": '"Bonheur Royale", "Brush Script MT", cursive',
            "montserrat":     '"Montserrat", "Helvetica Neue", Arial, sans-serif'
        };
        const DEFAULT_FONT_KEY = "montserrat";

        // Admin-set values from data-* attributes. No runtime mutation.
        const SETTINGS = {
            cx:     parseFloat(container.dataset.tmX)        || 0.455,
            cy:     parseFloat(container.dataset.tmY)        || 0.445,
            rot:    parseFloat(container.dataset.tmRotation) || 0,
            size:   parseFloat(container.dataset.tmSize)     || 0.033,
            weight: parseInt(container.dataset.tmWeight, 10) || 600,
            font:   container.dataset.tmFont                 || DEFAULT_FONT_KEY,
            color:  container.dataset.tmColor                || "#c0c0c0"
        };

        // Per-font geometry, written by the Engraving Admin app. A script face
        // and a sans need different placement on the same blade, and the
        // customer can switch between them with the Plain / Fancy selector — so
        // position, size, weight and rotation follow the chosen font.
        const FONT_VARIANTS = (function () {
            const raw = container.dataset.tmFonts;
            if (!raw) return {};
            try { return JSON.parse(raw) || {}; } catch (e) { return {}; }
        })();

        // Geometry for `slot` under `fontKey`, falling back to `base` when the
        // merchant has never tuned that font.
        function variantFor(slot, fontKey, base) {
            const v = FONT_VARIANTS[slot] && FONT_VARIANTS[slot][fontKey];
            if (!v) return base;
            const out = Object.assign({}, base);
            ["cx", "cy", "size", "weight", "rotation"].forEach(function (k) {
                const n = parseFloat(v[k]);
                if (isFinite(n)) out[k] = n;
            });
            return out;
        }

        // Per-line layout, written by the Engraving Admin app. Absent (or
        // malformed) means this product has never been configured for two
        // lines — the legacy shared-centre layout then runs unchanged.
        const LINES = (function () {
            const raw = container.dataset.tmLines;
            if (!raw) return null;
            let parsed;
            try { parsed = JSON.parse(raw); } catch (e) { return null; }
            if (!parsed || !parsed.line1 || !parsed.line2) return null;
            // Must match LINE_KEYS in engraving-admin/app/lib/engraving-geometry.js.
            // `w`/`h` were dropped with the box model — demanding them here made
            // every saved layout fail validation, so LINES was always null and
            // the per-line feature never rendered.
            const keys = ["cx", "cy", "rotation", "size", "weight"];
            for (const side of ["line1", "line2"]) {
                for (const k of keys) {
                    const n = parseFloat(parsed[side][k]);
                    if (!isFinite(n)) return null;
                    parsed[side][k] = n;
                }
                for (const k of ["color", "font"]) {
                    if (typeof parsed[side][k] !== "string" || !parsed[side][k]) return null;
                }
            }
            return parsed;
        })();

        const ctx = canvas.getContext("2d");
        let renderPending = null;
        let mockupReady = false;

        // Canvas text uses whatever font is loaded at draw time — re-render
        // once the Google Fonts have finished downloading so the preview
        // isn't drawn in a fallback face.
        if (document.fonts && document.fonts.load) {
            Promise.all([
                document.fonts.load('400 16px "Bonheur Royale"'),
                document.fonts.load('400 16px "Montserrat"'),
                document.fonts.load('600 16px "Montserrat"'),
                document.fonts.load('700 16px "Montserrat"'),
            ]).then(() => scheduleRender()).catch(() => {});
        }

        // The backing store MUST be sized from the canvas's LAYOUT box, because
        // that is the coordinate space render() draws into (it uses
        // canvas.clientWidth/clientHeight). getBoundingClientRect() returns the
        // VISUAL box, which ancestor transforms scale: the modal dialog animates
        // in from `transform: scale(0.98)`, and openModal() re-measures one rAF
        // after .is-open is added — mid-transition. That sized the store ~2%
        // smaller than the coordinates being drawn, so the browser stretched the
        // bitmap over the element and pushed the engraving down-and-right of the
        // calibrated spot. The mockup <img> is laid out by CSS and is immune, so
        // only the text moved. Layout size ignores transforms, so the two sources
        // can no longer disagree.
        function sizeCanvas() {
            const dpr  = window.devicePixelRatio || 1;
            const cssW = canvas.clientWidth;
            const cssH = canvas.clientHeight;
            canvas.width  = Math.max(1, Math.round(cssW * dpr));
            canvas.height = Math.max(1, Math.round(cssH * dpr));
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        }

        // Point text: the font size comes straight from `size` (a fraction of the
        // fitted mockup height), so a string renders at the same size however
        // long it is. Replaces the old fit-to-box shrink loop, which made the
        // size depend on how much the customer typed.
        // Whitespace between line 1's ink and line 2's ink, as a fraction of the
        // drawn mockup height. Line 2 is positioned from the MEASURED bounds of
        // both strings, not from a font size — that is what lets a script and a
        // sans sit together without colliding. A fraction of the drawn height
        // rather than a pixel count, so the gap stays proportional to the
        // engraving in this modal, in the editor preview and on a phone.
        // Keep in step with LINE_INK_GAP in
        // engraving-admin/app/lib/engraving-geometry.js.
        const LINE_INK_GAP = 0.0046;

        // Real ink bounds of one string, relative to the "middle" alignment
        // point. Per-string, so a line of caps reports no descent while one
        // containing g/y/p reports a large one.
        function measureInk(c, text, fontStack, weight, fontPx) {
            const prevFont = c.font, prevBaseline = c.textBaseline;
            c.font = weight + " " + fontPx + "px " + fontStack;
            c.textBaseline = "middle";
            const m = c.measureText(text);
            c.font = prevFont; c.textBaseline = prevBaseline;
            const asc = isFinite(m.actualBoundingBoxAscent) ? m.actualBoundingBoxAscent : fontPx / 2;
            const desc = isFinite(m.actualBoundingBoxDescent) ? m.actualBoundingBoxDescent : fontPx / 2;
            return { width: m.width, ascent: asc, descent: desc };
        }

        // Set while laserLayer() draws the letters as a plain white mask; records the largest font size.
        let laserMask = null;

        // Draw one line with an "engraved" look: a faint highlight above and
        // a slightly darker etch line below the main fill.
        function drawEngravedLine(c, text, x, yOffset, fontSize, fontStack, weight, color) {
            if (laserMask) {
                // The script face has one real weight; the laser's beam width (XZLaser) adds the stroke a
                // synthetic 600 used to fake, so the mask keeps the true 400.
                const w = /Bonheur/.test(fontStack) ? 400 : weight;
                c.font = `${w} ${fontSize}px ${fontStack}`;
                c.fillStyle = "#fff";
                c.fillText(text, x, yOffset);
                if (fontSize > laserMask.fpx) laserMask.fpx = fontSize;
                return;
            }
            c.font = `${weight} ${fontSize}px ${fontStack}`;

            // Shadow / etch line below the glyphs.
            c.fillStyle = "rgba(0, 0, 0, 0.32)";
            c.fillText(text, x, yOffset + 1);

            // Subtle highlight above the glyphs (bevel feel on metal).
            c.fillStyle = "rgba(255, 255, 255, 0.30)";
            c.fillText(text, x, yOffset - 1);

            // Main engraved fill.
            c.fillStyle = color;
            const prevAlpha = c.globalAlpha;
            c.globalAlpha = 0.92;
            c.fillText(text, x, yOffset);
            c.globalAlpha = prevAlpha;
        }

        // Draws the engraving text into the given context using CSS-pixel
        // coordinates. Shared by the on-screen canvas and the high-resolution
        // lens snapshot so the two never diverge. The text is real vector text,
        // so it re-rasterises crisply at whatever device scale the target
        // context is set to.
        function drawEngraving(c, cssW, cssH) {
            let text1 = (window.engravingText || "").trim();
            let text2 = window.engravingSecondSelected ? (window.engravingText2 || "").trim() : "";
            if (!text1 && !text2) return;

            const mw = mockupImg.naturalWidth;
            const mh = mockupImg.naturalHeight;
            if (!mw || !mh) return;

            const scale = Math.min(cssW / mw, cssH / mh);
            const drawW = mw * scale;
            const drawH = mh * scale;
            const offX  = (cssW - drawW) / 2;
            const offY  = (cssH - drawH) / 2;

            // fontKey1 is resolved below from the customer's selector; the
            // geometry is then looked up for that font.
            const BASE = { cx: SETTINGS.cx, cy: SETTINGS.cy, size: SETTINGS.size,
                           weight: SETTINGS.weight, rotation: SETTINGS.rot };
            // One face for both lines, from the single customer-facing selector.
            // It starts on the font the Engraving Admin app configured, so
            // leaving it alone renders exactly what the merchant set up.
            const fontKey = (fontSelect && FONT_NAME_TO_KEY[fontSelect.value])
                ? FONT_NAME_TO_KEY[fontSelect.value]
                : (SETTINGS.font || DEFAULT_FONT_KEY);
            const fontStack = FONT_MAP[fontKey] || FONT_MAP[DEFAULT_FONT_KEY];

            // The input handlers already did this, so it is normally a no-op. It
            // is repeated here so the drawn preview can never disagree with the
            // value that reaches the order, whatever set the globals.
            text1 = capitaliseForFont(text1, fontKey);
            text2 = capitaliseForFont(text2, fontKey);

            // Two configured lines, two independent centres. Only taken when the
            // customer actually typed two lines AND the merchant has saved a
            // per-line layout; otherwise the shared-centre path below runs, which
            // is byte-for-byte what shipped before this feature.
            if (text1 && text2 && LINES) {
                // Geometry follows the chosen font. Each line keeps its own
                // size, weight, rotation and colour under that one face.
                const L1 = variantFor("line1", fontKey, LINES.line1);
                const L2 = variantFor("line2", fontKey, LINES.line2);
                const px1 = offX + drawW * L1.cx;
                const py1 = offY + drawH * L1.cy;
                const f1 = drawH * L1.size;
                const f2 = drawH * L2.size;
                const rot1 = (L1.rotation * Math.PI) / 180;
                const rot2 = (L2.rotation * Math.PI) / 180;

                const m1 = measureInk(c, text1, fontStack, L1.weight, f1);
                const m2 = measureInk(c, text2, fontStack, L2.weight, f2);
                // bottom of line 1 -> gap -> top of line 2
                const dy = m1.descent + drawH * LINE_INK_GAP + m2.ascent;

                // Both drawn inside line 1's rotated frame: line 2 shares its
                // left edge and can never drift out of alignment.
                c.save();
                c.translate(px1, py1);
                c.rotate(rot1);
                c.textAlign = "left";
                c.textBaseline = "middle";
                drawEngravedLine(c, text1, 0, 0, f1, fontStack, L1.weight, L1.color);
                c.save();
                c.translate(0, dy);
                c.rotate(rot2 - rot1);
                drawEngravedLine(c, text2, 0, 0, f2, fontStack, L2.weight, L2.color);
                c.restore();
                c.restore();
                return;
            }

            // Shared-centre path: geometry follows the chosen font.
            const G = variantFor("single", fontKey, BASE);
            const px = offX + drawW * G.cx;
            const py = offY + drawH * G.cy;
            const fontPx = drawH * G.size;
            // Only reached when no per-line layout exists, where both lines
            // share one font — a size-based stack is safe there.
            const gapPx  = fontPx + drawH * LINE_INK_GAP;

            c.save();
            c.translate(px, py);
            c.rotate((G.rotation * Math.PI) / 180);
            // Left-anchored: (cx, cy) is the left edge of the baseline, so text
            // grows rightward from a fixed point and typing more characters never
            // shifts what is already there. Matches the admin app's render.
            c.textAlign = "left";
            c.textBaseline = "middle";

            if (text2) {
                drawEngravedLine(c, text1, 0, -gapPx / 2, fontPx, fontStack, G.weight, SETTINGS.color);
                drawEngravedLine(c, text2, 0,  gapPx / 2, fontPx, fontStack, G.weight, SETTINGS.color);
            } else {
                drawEngravedLine(c, text1, 0, 0, fontPx, fontStack, G.weight, SETTINGS.color);
            }
            c.restore();
        }

        // The laser look at `ss` drawing pixels per CSS pixel: the letters are drawn as a mask with exactly
        // drawEngraving's geometry, the blade photo under them is fitted the same way as the mockup <img>,
        // and XZLaser re-tones it. Returns the marked region (transparent around the letters) and where it
        // goes in CSS pixels, or null when there is nothing to draw. Throws on a tainted canvas; callers
        // then fall back to drawEngraving for good (laserBroken).
        let laserBroken = false;
        function laserLayer(cssW, cssH, ss) {
            const mw = mockupImg.naturalWidth, mh = mockupImg.naturalHeight;
            if (!mw || !mh || !cssW || !cssH) return null;
            const W = Math.max(1, Math.round(cssW * ss)), H = Math.max(1, Math.round(cssH * ss));
            const mc = document.createElement("canvas");
            mc.width = W; mc.height = H;
            const mx = mc.getContext("2d", { willReadFrequently: true });
            mx.scale(ss, ss);
            laserMask = { fpx: 0 };
            let fpx;
            try { drawEngraving(mx, cssW, cssH); fpx = laserMask.fpx * ss; } finally { laserMask = null; }
            if (!fpx) return null;
            const md = mx.getImageData(0, 0, W, H).data;
            let x0 = W, y0 = H, x1 = -1, y1 = -1;
            for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
                if (md[(y * W + x) * 4 + 3] < 8) continue;
                if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
            }
            if (x1 < 0) return null;
            const pad = Math.round(fpx * 1.2) + 4;
            x0 = Math.max(0, x0 - pad); y0 = Math.max(0, y0 - pad);
            x1 = Math.min(W, x1 + pad + 1); y1 = Math.min(H, y1 + pad + 1);
            const w = x1 - x0, h = y1 - y0;
            const pc = document.createElement("canvas");
            pc.width = w; pc.height = h;
            const px = pc.getContext("2d", { willReadFrequently: true });
            px.imageSmoothingEnabled = true;
            px.imageSmoothingQuality = "high";
            const scale = Math.min(cssW / mw, cssH / mh);
            px.setTransform(ss, 0, 0, ss, -x0, -y0);
            px.drawImage(mockupImg, (cssW - mw * scale) / 2, (cssH - mh * scale) / 2, mw * scale, mh * scale);
            const base = px.getImageData(0, 0, w, h);
            const mask = new Float32Array(w * h);
            for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) mask[y * w + x] = md[((y + y0) * W + x + x0) * 4 + 3] / 255;
            px.setTransform(1, 0, 0, 1, 0, 0);
            px.clearRect(0, 0, w, h);
            px.putImageData(XZLaser.mark(base, mask, fpx, Math.max(1, ss * scale)), 0, 0);
            return { canvas: pc, x: x0 / ss, y: y0 / ss, w: w / ss, h: h / ss };
        }

        // Paints the engraving into a CSS-pixel context: the laser look when it works, the flat drawing
        // otherwise. `ss` is the context's drawing pixels per CSS pixel.
        function paintEngraving(c, cssW, cssH, ss) {
            if (!laserBroken) {
                try {
                    const layer = laserLayer(cssW, cssH, ss);
                    if (layer) c.drawImage(layer.canvas, layer.x, layer.y, layer.w, layer.h);
                    return;
                } catch (err) {
                    laserBroken = true;
                }
            }
            drawEngraving(c, cssW, cssH);
        }

        // Snapshot the composite (mockup + engraving) into a data URL so the
        // magnifier lens can show a zoomed view via CSS background. The snapshot
        // is rendered at dpr * LENS_ZOOM so the lens shows genuinely
        // high-resolution content — vector text re-rasterised at the zoom scale
        // and the mockup sampled from its native resolution — instead of
        // upscaling a display-sized bitmap. Called after each successful render.
        // Safe-fallback if the canvas is tainted (CORS-locked mockup image).
        function updateLensSnapshot() {
            if (!lensEl || !mockupReady) return;
            try {
                const cssW = canvas.clientWidth;
                const cssH = canvas.clientHeight;
                const dpr = window.devicePixelRatio || 1;
                // Supersample so the lens shows real detail at LENS_ZOOM, but cap
                // the longest edge: without this a large preview on a retina
                // screen produces a multi-megapixel PNG that has to be
                // re-sampled on every repaint of the lens.
                let ss = dpr * LENS_ZOOM;
                const longestCss = Math.max(cssW, cssH);
                if (longestCss * ss > MAX_LENS_SNAPSHOT_PX) {
                    ss = MAX_LENS_SNAPSHOT_PX / longestCss;
                }
                const off = document.createElement("canvas");
                off.width  = Math.max(1, Math.round(cssW * ss));
                off.height = Math.max(1, Math.round(cssH * ss));
                const octx = off.getContext("2d");
                if (!octx) return;
                octx.scale(ss, ss);
                octx.imageSmoothingEnabled = true;
                octx.imageSmoothingQuality = "high";
                const mw = mockupImg.naturalWidth;
                const mh = mockupImg.naturalHeight;
                if (mw && mh) {
                    const scale = Math.min(cssW / mw, cssH / mh);
                    const drawW = mw * scale;
                    const drawH = mh * scale;
                    const offX  = (cssW - drawW) / 2;
                    const offY  = (cssH - drawH) / 2;
                    octx.drawImage(mockupImg, offX, offY, drawW, drawH);
                }
                // Re-render the engraving at the supersampled scale rather than
                // copying the display-resolution canvas bitmap.
                paintEngraving(octx, cssW, cssH, ss);
                lensEl.style.backgroundImage = `url(${off.toDataURL("image/png")})`;
            } catch (err) {
                // Tainted canvas or other failure — disable the lens silently.
            }
        }

        // How long the last full-resolution laser pass took. On a slow phone the preview draws a quick
        // 1x pass while the customer types and the sharp pass once they pause. The lens snapshot (the
        // costliest draw: the laser at twice the screen's resolution) is made only when the lens is
        // about to be used, so typing never waits for it.
        let laserMs = 0;
        let settleTimer = null;
        let lensDirty = true;
        signal.addEventListener('abort', () => clearTimeout(settleTimer), { once: true });
        const freshLens = () => { if (lensDirty) { lensDirty = false; updateLensSnapshot(); } };
        listen(stage, "pointerenter", freshLens);
        listen(stage, "pointerdown", freshLens, { capture: true });

        function render(sharp) {
            if (!mockupReady) return;
            const cssW = canvas.clientWidth;
            const cssH = canvas.clientHeight;
            ctx.clearRect(0, 0, cssW, cssH);

            const text1 = (window.engravingText || "").trim();
            const text2 = window.engravingSecondSelected ? (window.engravingText2 || "").trim() : "";

            if (placeholder) {
                placeholder.classList.toggle("is-hidden", !!(text1 || text2));
            }

            const dpr = window.devicePixelRatio || 1;
            const quick = sharp !== true && !laserBroken && dpr > 1 && laserMs > 40;
            const t0 = performance.now();
            paintEngraving(ctx, cssW, cssH, quick ? 1 : dpr);
            if (!quick) laserMs = performance.now() - t0;
            lensDirty = true;
            clearTimeout(settleTimer);
            if (quick) {
                settleTimer = setTimeout(() => { if (!signal.aborted) render(true); }, 180);
            } else if (lensEl && lensEl.classList.contains("is-visible")) {
                settleTimer = setTimeout(() => { if (!signal.aborted) freshLens(); }, 180);
            }
        }

        scheduleRender = function () {
            if (renderPending) cancelAnimationFrame(renderPending);
            renderPending = frame(() => {
                renderPending = null;
                render();
            });
        };

        if (mockupImg.complete && mockupImg.naturalWidth > 0) {
            mockupReady = true;
        } else {
            listen(mockupImg, "load", () => {
                mockupReady = true;
                sizeCanvas();
                render();
            }, { once: true });
            listen(mockupImg, "error", () => {
                if (placeholder) placeholder.textContent = "Preview unavailable";
            }, { once: true });
        }

        if (stage && "ResizeObserver" in window) {
            const resizeObserver = new ResizeObserver(() => {
                if (!signal.aborted) { sizeCanvas(); render(); }
            });
            resizeObserver.observe(stage);
            signal.addEventListener('abort', () => resizeObserver.disconnect(), { once: true });
        } else {
            listen(window, "resize", () => { sizeCanvas(); render(); });
        }

        listen(btnOneLine, "click", () => {
            updatePreviewButtonState();
            scheduleRender();
        });
        listen(btnTwoLines, "click", () => {
            updatePreviewButtonState();
            scheduleRender();
        });
        listen(btnNoEngraving, "click", updatePreviewButtonState);

        // Publish the chosen font for the order and re-render the preview.
        //
        // Switching to Fancy also capitalises text already typed, on both lines.
        // Switching back to Plain does NOT lower it again: the capital may well
        // be the customer's own, and quietly rewriting what they typed is worse
        // than leaving a capital they did not ask for.
        if (fontSelect) {
            listen(fontSelect, "change", () => {
                window.engravingFont = fontSelect.value;
                window.engravingText = enforceFontCase(engraveInput);
                window.engravingText2 = enforceFontCase(engraveSecondInput);
                scheduleRender();
                // The canvas draws in whatever face is loaded at that moment. If the chosen face is
                // still downloading, draw again once it has arrived (the init-time load above may
                // already have settled), so the preview never stays in a fallback script.
                if (document.fonts && document.fonts.load) {
                    const fancy = currentFontKey() === "bonheur-royale";
                    const faces = fancy ? ['400 16px "Bonheur Royale"']
                        : [SETTINGS.weight, 400, 600, 700].filter((w, i, all) => all.indexOf(w) === i)
                            .map((w) => `${w} 16px "Montserrat"`);
                    Promise.all(faces.map((face) => document.fonts.load(face)))
                        .then(() => { if (!signal.aborted) scheduleRender(); })
                        .catch(() => {});
                }
            });
        }

        // Magnifier lens — show on hover, follow cursor, hide on leave.
        //
        // mousemove fires far faster than the screen refreshes, so the handler
        // only records the pointer and defers the style write to the next frame.
        // The lens is moved with `transform`, which the compositor handles on its
        // own; setting `left`/`top` forced a layout on every event, and the lens
        // background is a large supersampled snapshot, so each of those layouts
        // dragged a full repaint of it along behind them.
        resetLens = bindEngravingLens(stage, lensEl, listen, frame, LENS_SIZE, LENS_ZOOM);
        signal.addEventListener('abort', resetLens, { once: true });

        updatePreviewButtonState();
        sizeCanvas();
        if (mockupReady) render();

}

if (!customElements.get('engraving-option-component')) {
    class EngravingOptionComponent extends HTMLElement {
        connectedCallback() {
            this.lifecycle?.abort();
            const lifecycle = this.lifecycle = new AbortController();
            // Also works when the custom element is parsed after its definition.
            queueMicrotask(() => {
                if (!lifecycle.signal.aborted && this.isConnected) initializeEngraving(this, lifecycle.signal);
            });
        }
        disconnectedCallback() {
            this.lifecycle?.abort();
            // Prevent a later non-engravable product from inheriting old line fees.
            // A moved owner restores its existing input state when it reconnects.
            if (!activeEngravingOwner || activeEngravingOwner === this) {
                activeEngravingOwner = undefined;
                window.engravingSelected = false;
                window.engravingSecondSelected = false;
                window.engravingText = '';
                window.engravingText2 = '';
                window.knife_num = 1;
            }
            const modal = this.querySelector('#engraving-modal');
            if (modal?.classList.contains('is-open')) {
                modal.classList.remove('is-open');
                modal.setAttribute('aria-hidden','true');
                if (!document.querySelector('.engraving-modal.is-open')) document.body.classList.remove('engraving-modal-open');
            }
        }
    }
    customElements.define('engraving-option-component', EngravingOptionComponent);
}
